// Body work picker: tap the damaged parts on a picture of the truck / trailer (or type their numbers),
// choose what to do with each (repair, replace, refinish …) and save it as a "Body work" job.
// Office: priced lines (body / paint rate, paint & materials, hazardous waste) — optionally on their own invoice.
// Mechanic: marks the damage only; the office prices it with "Price it".
import { useEffect, useMemo, useState } from 'react';
import { api, asset, type Row } from '../lib';
import { Button, Modal, useToast } from '../ui';
import { BODY_MODELS, pickBodyModel, defaultOps, bodyLines, bodyOpsFor, partNumberFor, type BodyOps } from '../../shared/bodyGuide';

type Zone = { n: string; p: string };
type View = { id: string; title: string; w: number; h: number; img: string; zones: Zone[] };
const cache = new Map<string, Promise<View[]>>();
function loadModel(id: string) {
  if (!cache.has(id)) cache.set(id, fetch(asset(`/body/${id}.json`)).then(r => { if (!r.ok) throw new Error('Picture not found'); return r.json(); }).catch(e => { cache.delete(id); throw e; }));
  return cache.get(id)!;
}
const mime = (b64: string) => (b64.startsWith('iVBOR') ? 'image/png' : 'image/jpeg');
function center(p: string) {
  const pts = p.trim().split(/\s+/).map(s => s.split(',').map(Number));
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[(i + 1) % pts.length];
    const f = x0 * y1 - x1 * y0; a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f;
  }
  if (Math.abs(a) < 1e-6) return { x: pts.reduce((s, q) => s + q[0], 0) / pts.length, y: pts.reduce((s, q) => s + q[1], 0) / pts.length };
  return { x: cx / (3 * a), y: cy / (3 * a) };
}
const hrs = (h: number) => (h / 100).toFixed(1);

/** Which picture fits this work order: the truck (or its trailer). */
export function defaultBodyModel(w: Row, trailer: boolean) {
  return trailer
    ? pickBodyModel({ make: w.trailer_make, model: w.trailer_model, year: w.trailer_year, type: w.trailer_type || 'trailer', trailer: true })
    : pickBodyModel({ make: w.make, model: w.model, year: w.year, type: w.unit_type, trailer: /trailer|van|reefer/i.test(String(w.unit_type || '')) && !/truck|tractor/i.test(String(w.unit_type || '')) });
}

export function BodyPicker({ wo, mech = false, job, onClose, onDone }: { wo: Row; mech?: boolean; job?: Row; onClose: () => void; onDone: (r: Row) => void }) {
  const toast = useToast();
  const startTrailer = job ? !!job.on_trailer : false;
  const [trailer, setTrailer] = useState(startTrailer);
  const [model, setModel] = useState<string>(job?.body_model || defaultBodyModel(wo, startTrailer));
  const [views, setViews] = useState<View[] | null>(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('lh');
  const [typed, setTyped] = useState('');
  const [sel, setSel] = useState<{ part: string; ops: BodyOps }[]>(() => { try { return job?.body_parts ? JSON.parse(job.body_parts) : []; } catch { return []; } });
  const [separate, setSeparate] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true; setViews(null); setErr('');
    loadModel(model).then(v => { if (live) { setViews(v); if (!v.some(x => x.id === view)) setView(v[0]?.id || 'lh'); } }).catch(e => live && setErr((e as Error).message));
    return () => { live = false; };
  }, [model]);

  const cur = views?.find(v => v.id === view) || views?.[0];
  const picked = (part: string) => sel.some(s => s.part === part);
  const toggle = (part: string) => setSel(s => s.some(x => x.part === part) ? s.filter(x => x.part !== part) : [...s, { part, ops: defaultOps(part) }]);
  const setOps = (part: string, ops: BodyOps) => setSel(s => s.map(x => x.part === part ? { ...x, ops } : x));
  const lines = useMemo(() => sel.flatMap(s => bodyLines(s.part, s.ops)), [sel]);
  const bodyH = lines.filter(l => !l.paint).reduce((a, l) => a + l.hours, 0), paintH = lines.filter(l => l.paint).reduce((a, l) => a + l.hours, 0);
  const typeNumber = () => {
    const n = Number(typed.trim());
    const z = cur?.zones[n - 1];
    if (!z) { toast(`No part #${typed} on this side`, 'err'); return; }
    toggle(z.n); setTyped('');
  };
  async function save() {
    if (!sel.length) { toast('Tap the damaged parts first', 'err'); return; }
    setBusy(true);
    try {
      const r = await api<Row>(`/api/work-orders/${wo.id}/body`, { body: { items: sel, model, trailer, separate: separate && !job, job_id: job?.id } });
      onDone(r);
    } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  }

  const trucks = BODY_MODELS.filter(m => !m.trailer), trailers = BODY_MODELS.filter(m => m.trailer);
  return (
    <Modal title={job ? `Body work — ${mech ? 'damage' : 'price it'}` : mech ? 'Mark body damage' : 'Body work'} onClose={onClose} wide>
      <div className="body-pick">
        <div className="body-top">
          {wo.trailer_id && <span className="side-pick" role="group" aria-label="Truck or trailer">
            {[[false, 'Truck'], [true, 'Trailer']].map(([t, l]) => <button key={String(l)} type="button" className={trailer === t ? 'on' : ''}
              onClick={() => { setTrailer(t as boolean); setModel(defaultBodyModel(wo, t as boolean)); }}>{l as string}</button>)}
          </span>}
          <select className="mini-select" value={model} onChange={e => setModel(e.target.value)} aria-label="Truck / trailer picture">
            <optgroup label="Trucks">{trucks.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</optgroup>
            <optgroup label="Trailers">{trailers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</optgroup>
          </select>
        </div>
        {err && <p className="err">Couldn't load the picture ({err}). Check the connection and try again.</p>}
        {!views && !err && <p className="muted">Loading picture…</p>}
        {views && cur && <>
          <div className="body-tabs" role="tablist">{views.map(v => <button key={v.id} type="button" role="tab" aria-selected={v.id === cur.id} className={'chip' + (v.id === cur.id ? ' on' : '')} onClick={() => setView(v.id)}>
            {v.title}{(() => { const k = v.zones.filter(z => picked(z.n)).length; return k ? ` (${k})` : ''; })()}</button>)}</div>
          <svg className="body-svg" viewBox={`0 0 ${cur.w} ${cur.h}`} role="img" aria-label={cur.title} style={{ aspectRatio: `${cur.w} / ${cur.h}`, maxWidth: `calc(62vh * ${cur.w / cur.h})` }}>
            <image href={`data:${mime(cur.img)};base64,${cur.img}`} width={cur.w} height={cur.h} />
            {cur.zones.map(z => <polygon key={z.n} points={z.p} className={picked(z.n) ? 'on' : ''} onClick={() => toggle(z.n)}><title>{z.n}</title></polygon>)}
            {cur.zones.map((z, i) => { const c = center(z.p); const nr = Math.min(cur.w / 58, cur.h / 15); return <g key={'n' + i} className={'zn' + (picked(z.n) ? ' on' : '')} onClick={() => toggle(z.n)}>
              <circle cx={c.x} cy={c.y} r={nr} /><text x={c.x} y={c.y + nr * 0.4} style={{ fontSize: nr * 1.1 }}>{i + 1}</text></g>; })}
          </svg>
          <div className="body-type">
            <input inputMode="numeric" placeholder="Part #" value={typed} onChange={e => setTyped(e.target.value.replace(/\D/g, ''))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); typeNumber(); } }} aria-label="Type the part number from the picture" />
            <Button type="button" onClick={typeNumber}>Add #</Button>
            <details className="body-legend"><summary>All parts on this side</summary>
              <ol>{cur.zones.map(z => <li key={z.n}><label className="check inline"><input type="checkbox" checked={picked(z.n)} onChange={() => toggle(z.n)} /> {z.n}</label></li>)}</ol></details>
          </div>
        </>}

        <h3 className="body-h">Damaged parts ({sel.length})</h3>
        {!sel.length && <p className="muted small">Tap a part on the picture, or type its number.</p>}
        <ul className="body-list">{sel.map(s => {
          const can = bodyOpsFor(s.part), o = s.ops, pn = partNumberFor(model, s.part);
          const set = (k: Partial<BodyOps>) => setOps(s.part, { ...o, ...k });
          const ls = bodyLines(s.part, o);
          return <li key={s.part}>
            <div className="bl-head"><b>{s.part}</b>{pn && <small className="muted"> · part # {pn}</small>}
              {!mech && <small className="muted"> · {hrs(ls.reduce((a, l) => a + l.hours, 0))} h</small>}
              <button type="button" className="linkish" onClick={() => toggle(s.part)}>remove</button></div>
            <div className="chip-row">
              {can.repair && (['light', 'medium', 'heavy'] as const).map(r => <button key={r} type="button" className={'chip' + (!o.rr && o.repair === r ? ' on' : '')} onClick={() => set({ rr: false, repair: o.repair === r ? '' : r })}>Repair {r}</button>)}
              <button type="button" className={'chip' + (o.rr ? ' on' : '')} onClick={() => set({ rr: !o.rr, ...(o.rr ? {} : { repair: '', ri: false }) })}>Replace (R&R)</button>
              {can.ri && <button type="button" className={'chip' + (o.ri && !o.rr ? ' on' : '')} disabled={!!o.rr} onClick={() => set({ ri: !o.ri })}>Remove & reinstall</button>}
              {can.transfer && o.rr && <button type="button" className={'chip' + (o.transfer ? ' on' : '')} onClick={() => set({ transfer: !o.transfer })}>Transfer parts</button>}
              {can.refinish && <button type="button" className={'chip' + (o.refinish ? ' on' : '')} onClick={() => set({ refinish: !o.refinish })}>Paint</button>}
              {can.refinish && <button type="button" className={'chip' + (o.blend ? ' on' : '')} onClick={() => set({ blend: !o.blend })}>Blend next panel</button>}
            </div>
          </li>;
        })}</ul>

        {!mech && sel.length > 0 && <div className="body-sum">
          <span>Body labor <b>{hrs(bodyH)} h</b></span><span>Paint <b>{hrs(paintH)} h</b></span>
          {paintH > 0 && <span className="muted small">+ paint & materials and hazardous waste (Settings → Body shop)</span>}
          <span className="muted small">Hours are a starting point — change them on the lines.</span>
        </div>}
        {!mech && !job && <label className="check"><input type="checkbox" checked={separate} onChange={e => setSeparate(e.target.checked)} /> Separate invoice — put the body work on its own work order</label>}
        {mech && <p className="muted small">The office gets a notification and prices it.</p>}
        <div className="actions-row">
          <Button kind="primary" busy={busy} onClick={save}>{mech ? 'Send to office' : job ? 'Price it' : 'Add body work'}</Button>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}

/** The parts a mechanic / the office marked on a body job, as a short line. */
export function bodyPartsText(job: Row) {
  try { return (JSON.parse(job.body_parts || '[]') as { part: string }[]).map(p => p.part).join(', '); } catch { return ''; }
}
